/**
 * ショットコメントのディープリンク（`/{projectId}/{shotId}?comment={commentId}`）
 * を開いたときの適用処理。URL 生成側は `utils/deepLink.ts` の `buildShotCommentLink`。
 *
 * - コメント初期ページの読み込み・初回最下部スクロールを待ってから適用する
 * - 対象コメントが読み込み済みページに無ければ単体 GET で一覧へ差し込む
 * - フィルタ・パネルを整え、要素の出現を待って該当コメントへスクロール +
 *   一時ハイライトする
 * - ピン付きコメントなら既存のピンクリックと同じ経路でピン先メディアを再生する
 * - 適用後は `comment` パラメータを replace で外す（ライブラリの `file` と同じ扱い。
 *   Preview の location.state（shot / previewQueue 等）は引き継ぐ）
 *
 * Preview 内部の state には直接触れず、すべてコールバックで受け取る
 * （Preview.tsx への追加を最小にし、並行開発との衝突を減らすため）。
 */
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type MutableRefObject,
} from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";

import type { ShotComment } from "../../../app/services/shotComments";

/** スクロール先要素の出現を待つポーリング間隔（フィルタ切替・パネル開閉の描画待ち） */
const SCROLL_POLL_INTERVAL_MS = 150;
/** ポーリングの上限回数（これを超えたらスクロールせず適用を終える） */
const SCROLL_POLL_MAX_ATTEMPTS = 20;
/** ハイライト表示時間（CSS アニメーション 2.4s より少し長く） */
const HIGHLIGHT_DURATION_MS = 2600;

export interface ShotCommentDeepLinkParams {
  /** URL の :shotId。切り替わったら適用状態をリセットする */
  shotId: string | undefined;
  /** コメント初期ページの読み込み・初回最下部スクロールが済んだか */
  commentsReady: boolean;
  /** 読み込み済みコメント（単体取得の要否判定に使う） */
  allComments: ShotComment[];
  /** 一覧に無いコメントを単体取得して一覧へ差し込む。見つからなければ null */
  // eslint-disable-next-line no-unused-vars
  fetchAndInjectComment: (commentId: string) => Promise<ShotComment | null>;
  /** 対象コメントが見えるようフィルタ・パネルを整える（冪等であること） */
  // eslint-disable-next-line no-unused-vars
  revealComment: (comment: ShotComment) => void;
  /** 描画済みなら該当コメントへスクロールして true、未描画なら false */
  // eslint-disable-next-line no-unused-vars
  scrollToComment: (commentId: string) => boolean;
  /** ピン付きコメントをピン位置から再生する（ピン無しは何もしない） */
  // eslint-disable-next-line no-unused-vars
  playPinnedComment: (comment: ShotComment) => void;
  /**
   * 適用中 true になるフラグ。コメント一覧の「件数変化で最下部へ」等の
   * 自動スクロールはこれを見て抑止する（差し込みやパネル展開で件数が変わり、
   * 対象コメントへのスクロールが上書きされるのを防ぐ）。
   */
  autoScrollLockRef?: MutableRefObject<boolean>;
}

export function useShotCommentDeepLink(params: ShotCommentDeepLinkParams): {
  /** 一時ハイライト中のコメント ID（描画側で class を付ける） */
  highlightedCommentId: string | null;
} {
  const { shotId, commentsReady } = params;
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const commentParam = searchParams.get("comment");

  /** 解決済みで「スクロール・再生待ち」の対象コメント */
  const [target, setTarget] = useState<ShotComment | null>(null);
  const [highlightedCommentId, setHighlightedCommentId] = useState<
    string | null
  >(null);

  // コールバック・state は ref 経由で最新を参照し、ポーリング中の effect
  // 再実行（interval の張り直し）を避ける。
  const paramsRef = useRef(params);
  paramsRef.current = params;
  const locationStateRef = useRef<unknown>(location.state);
  locationStateRef.current = location.state;

  /** このショットで適用済みのコメント ID（再描画での重複適用ガード） */
  const appliedCommentIdRef = useRef<string | null>(null);

  /** 自動スクロール抑止フラグの更新（未指定なら何もしない） */
  const setAutoScrollLock = useCallback((locked: boolean) => {
    const ref = paramsRef.current.autoScrollLockRef;
    if (ref) ref.current = locked;
  }, []);

  useEffect(() => {
    appliedCommentIdRef.current = null;
    setTarget(null);
    setAutoScrollLock(false);
  }, [shotId, setAutoScrollLock]);

  /** `comment` パラメータだけを外す（state は引き継ぐ） */
  const clearParam = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("comment");
        return next;
      },
      { replace: true, state: locationStateRef.current },
    );
  }, [setSearchParams]);

  // 対象コメントの解決（読み込み済みに無ければ単体取得して差し込む）
  useEffect(() => {
    if (!commentParam || !commentsReady) return;
    if (appliedCommentIdRef.current === commentParam) return;
    appliedCommentIdRef.current = commentParam;
    setAutoScrollLock(true);

    const existing = paramsRef.current.allComments.find(
      (c) => c.id === commentParam,
    );
    if (existing) {
      setTarget(existing);
      return;
    }
    void paramsRef.current.fetchAndInjectComment(commentParam).then(
      (fetched) => {
        if (fetched) {
          setTarget(fetched);
        } else {
          toast.error(
            "リンク先のコメントが見つからないか、削除されています",
          );
          clearParam();
          setAutoScrollLock(false);
          // 再クリックで再試行できるように重複ガードを解除する
          appliedCommentIdRef.current = null;
        }
      },
    );
  }, [commentParam, commentsReady, clearParam, setAutoScrollLock]);

  // フィルタ・パネルを整え、要素の出現を待ってスクロール → ハイライト → ピン再生
  useEffect(() => {
    if (!target) return;
    paramsRef.current.revealComment(target);

    let attempts = 0;
    const finish = (scrolled: boolean) => {
      window.clearInterval(timer);
      if (scrolled) {
        setHighlightedCommentId(target.id);
        window.setTimeout(() => {
          setHighlightedCommentId((prev) =>
            prev === target.id ? null : prev,
          );
          // スムーズスクロールと注視が終わるまで自動最下部スクロールを抑止
          setAutoScrollLock(false);
        }, HIGHLIGHT_DURATION_MS);
        paramsRef.current.playPinnedComment(target);
      } else {
        setAutoScrollLock(false);
      }
      clearParam();
      setTarget(null);
      // 適用が終わったら重複ガードを解除する。パラメータは直前の clearParam で
      // 消えるため再適用ループにはならず、同じリンクをもう一度クリックしたときに
      // 改めてスクロール・ハイライトできるようになる（ガードを残すと 2 回目以降は
      // ?comment= が URL に残ったまま何も起きない）。
      appliedCommentIdRef.current = null;
    };
    const tick = () => {
      attempts += 1;
      if (paramsRef.current.scrollToComment(target.id)) {
        finish(true);
      } else if (attempts >= SCROLL_POLL_MAX_ATTEMPTS) {
        finish(false);
      }
    };
    const timer = window.setInterval(tick, SCROLL_POLL_INTERVAL_MS);
    tick();
    return () => window.clearInterval(timer);
  }, [target, clearParam, setAutoScrollLock]);

  return { highlightedCommentId };
}

export default useShotCommentDeepLink;
